// Approval of purchase orders and purchase requests (docs/plans/po-approvals.md,
// purchase-requests.md): a document's steps and the Approve / Reject buttons for the person
// whose turn it is, and the Approvals inbox. Decisions are made online by decide_document;
// the device only shows them. Boards POWaiting, POReject, POApprovals, POApprovalsPhone.
import type { PurchaseOrderRow, PurchaseRequestRow, RowOf } from '@ims/data'
import { tenantApi } from '@ims/remote'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  IconButton,
  IconTile,
  Kbd,
  ListFrame,
  ListToolbar,
  Modal,
  PageHeader,
  Pagination,
  RowActions,
  RowNote,
  RowTitle,
  SearchInput,
  Segmented,
  SkeletonTable,
  TD,
  TH,
  TR,
  Table,
  Textarea,
  cx,
  useToast,
} from '@ims/ui'
import { Check, ClipboardList, Clock, Hand, Send, Stamp, X } from '@ims/ui/icons'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { errorMessage, useFeature, useLive, useOnline, useSettings } from '../hooks.ts'
import { createApi } from '../lib/api.ts'
import {
  ROLE_LABEL,
  ROLE_WORDS,
  approvalSteps,
  mayDecide,
  nextStep,
  type Approver,
  type Decision,
  type Step,
} from '../lib/approvals.ts'
import { dateTime, money, timeAgo } from '../lib/format.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { usePaged } from '../lib/paging.ts'
import { useStaffNames } from '../lib/staff.ts'
import { useSession, useUser } from '../session.tsx'
import { useRole, useStore, useWorkspace } from '../workspace.tsx'
import { SectionTabs } from '../layout/sections.tsx'

type Rule = RowOf<'approval_rules'>
type Approval = RowOf<'document_approvals'>

const APPROVAL_TABLES = [
  'approval_rules',
  'document_approvals',
  'purchase_orders',
  'purchase_requests',
] as const

/** What is approved: a purchase order or a purchase request. */
export interface ApprovalDoc {
  docType: 'purchase_order' | 'requisition'
  id: string
  number: string | null
  status: string
  total_paisa: number
  created_by?: string | null
  created_at: string
}

export const poDoc = (o: PurchaseOrderRow): ApprovalDoc => ({ docType: 'purchase_order', ...o })
export const requestDoc = (r: PurchaseRequestRow): ApprovalDoc => ({ docType: 'requisition', ...r })
const WORD = { purchase_order: 'order', requisition: 'request' } as const
export const docPath = (d: Pick<ApprovalDoc, 'docType' | 'id'>) =>
  d.docType === 'purchase_order' ? `/purchase-orders/${d.id}` : `/purchase-requests/${d.id}`

/** The rules, this person as an approver (role and own limit), and whether the feature is on. */
export function useApprovalContext() {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const user = useUser()
  const role = useRole()
  const online = useOnline()
  const featureOn = useFeature('purchase_control')
  const rules =
    useLive(
      (r) => r.list('approval_rules', { orderBy: { column: 'level' }, limit: 50 }),
      [],
      ['approval_rules'],
    ).data?.rows ?? []
  // The person's own limit lives on their membership, read online (it is not synced).
  const [limit, setLimit] = useState<{ tenant: string; value: number | null } | null>(null)
  const tenantId = current?.tenant_id ?? ''
  useEffect(() => {
    if (!tenantId || !online) return
    let active = true
    void tenantApi(platform.supabase)
      .myApprovalLimit(tenantId)
      .then((value) => active && setLimit({ tenant: tenantId, value }))
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [platform.supabase, tenantId, online])
  const me: Approver = {
    userId: user.id,
    role,
    limitPaisa: limit?.tenant === tenantId ? limit.value : null,
  }
  return { rules: rules as Rule[], featureOn, me, online }
}

/** Approve or reject, online, then pull so this device shows the outcome at once. */
function useDecide() {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const { engine } = useStore()
  const toast = useToast()
  const [busy, setBusy] = useState<string | null>(null)
  const decide = async (
    doc: Pick<ApprovalDoc, 'docType' | 'id' | 'number'>,
    decision: 'approved' | 'rejected',
    note: string | null,
  ) => {
    if (!current) return false
    setBusy(doc.id)
    const name = doc.number ?? (doc.docType === 'purchase_order' ? 'Order' : 'Request')
    try {
      const result = await tenantApi(platform.supabase).decideDocument(
        current.tenant_id,
        doc.docType,
        doc.id,
        decision,
        note,
      )
      toast.success(
        decision === 'rejected'
          ? `${name} rejected`
          : result.status === 'approved'
            ? `${name} approved`
            : `Step ${result.level} approved`,
        result.status === 'approved'
          ? {
              detail:
                doc.docType === 'purchase_order'
                  ? 'It can be sent to the supplier now.'
                  : 'Purchase orders can be made from it now.',
            }
          : undefined,
      )
      void engine.syncNow().catch(() => undefined)
      // The next approver, or the maker, hears it now rather than at the next cron minute.
      void createApi(platform.apiUrl, platform.supabase)
        .pushKick(current.tenant_id)
        .catch(() => undefined)
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(null)
    }
  }
  return { decide, busy }
}

/** Rejecting needs a reason the maker sees. */
export function RejectDialog({
  order,
  maker,
  onClose,
  onRejected,
}: {
  order: Pick<ApprovalDoc, 'docType' | 'id' | 'number' | 'total_paisa'>
  maker: string
  onClose: () => void
  onRejected?: () => void
}) {
  const settings = useSettings()
  const { decide, busy } = useDecide()
  const [why, setWhy] = useState('')
  return (
    <Modal
      open
      onClose={onClose}
      title={`Reject ${order.number ?? `this ${WORD[order.docType]}`}?`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={busy === order.id}
            disabled={!why.trim()}
            onClick={async () => {
              if (await decide(order, 'rejected', why.trim())) {
                onRejected?.()
                onClose()
              }
            }}
          >
            Reject
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-sm">
        <p className="text-muted">
          {money(order.total_paisa, settings)} · made by {maker}
        </p>
        <Field label={`Why (${maker} sees this)`}>
          {(id) => (
            <Textarea
              id={id}
              rows={3}
              maxLength={500}
              value={why}
              onChange={(e) => setWhy(e.target.value)}
            />
          )}
        </Field>
        <p className="text-xs text-muted">
          {order.docType === 'purchase_order'
            ? 'A rejected order stays as it is. It can be revised into a new order, which asks again.'
            : 'A rejected request stays as it is. It can be copied into a new request, which asks again.'}
        </p>
      </div>
    </Modal>
  )
}

/** The decisions on one document, oldest first. */
function useDecisions(docType: ApprovalDoc['docType'], docId: string): Approval[] {
  return (
    useLive(
      (r) =>
        r.list('document_approvals', {
          where: { doc_type: docType, doc_id: docId },
          orderBy: { column: 'level' },
          limit: 10,
        }),
      [docType, docId],
      ['document_approvals'],
    ).data?.rows ?? []
  )
}

/** A document's approval: who made it, each step, and the buttons for the one whose turn it is. */
export function ApprovalCard({ doc: order }: { doc: ApprovalDoc }) {
  const settings = useSettings()
  const names = useStaffNames()
  const { rules, me, online } = useApprovalContext()
  const decisions = useDecisions(order.docType, order.id)
  const { decide, busy } = useDecide()
  const [rejecting, setRejecting] = useState(false)
  const steps = approvalSteps(rules, order.total_paisa, order.docType)
  // Nothing to show until it waits for approval or someone has decided (a request approved
  // at once because no step applied has no decisions).
  if (order.status !== 'submitted' && decisions.length === 0) return null
  const name = (id: string | null | undefined) => (id && names[id]) || 'Someone'
  const maker = name(order.created_by)
  const can = mayDecide(order, steps, decisions, me, WORD[order.docType])
  const next = order.status === 'submitted' ? nextStep(steps, decisions) : null
  // Steps that applied when it was decided stay shown even if the rules changed since.
  const shown: Step[] = [
    ...steps,
    ...decisions
      .filter((d) => !steps.some((s) => s.level === d.level))
      .map((d) => ({ level: d.level, role: 'manager' as const })),
  ].sort((a, b) => a.level - b.level)
  return (
    <Card>
      <h3 className="text-[15px] font-semibold">Approval</h3>
      <p className="text-sm text-muted">
        {money(order.total_paisa, settings)}{' '}
        {shown.length === 0
          ? 'waits for approval.'
          : shown.length === 1
            ? 'needs one step.'
            : `needs ${shown.length} steps.`}
      </p>
      <ol className="mt-2">
        <StepRow
          icon={<Send className="size-4" />}
          tone="success"
          title={order.docType === 'purchase_order' ? 'Made' : 'Asked'}
          who={maker}
          line={`Submitted · ${dateTime(order.created_at, settings)}`}
        />
        {shown.map((s) => {
          const d = decisions.find((x) => x.level === s.level)
          const waiting = !d && next?.level === s.level
          return (
            <StepRow
              key={s.level}
              icon={
                d?.decision === 'rejected' ? (
                  <X className="size-4" />
                ) : d ? (
                  <Check className="size-4" />
                ) : (
                  <Clock className="size-4" />
                )
              }
              tone={
                d?.decision === 'rejected'
                  ? 'danger'
                  : d
                    ? 'success'
                    : waiting
                      ? 'warning'
                      : 'neutral'
              }
              title={`Step ${s.level} · ${ROLE_LABEL[s.role]}`}
              who={d ? name(d.decided_by) : ''}
              line={
                d
                  ? `${d.decision === 'approved' ? 'Approved' : 'Rejected'}${d.note ? `: “${d.note}”` : ''} · ${dateTime(d.created_at, settings)}`
                  : waiting
                    ? can.ok
                      ? 'Waiting for you'
                      : `Waiting for ${ROLE_WORDS[s.role]}`
                    : order.status === 'submitted'
                      ? 'After the step above'
                      : 'Not needed'
              }
            />
          )
        })}
      </ol>
      {order.status === 'submitted' && can.ok && (
        <>
          <div className="mt-3 flex gap-2">
            <Button
              className="flex-1"
              icon={<Check className="size-4" />}
              loading={busy === order.id}
              disabled={!online}
              onClick={() => void decide(order, 'approved', null)}
            >
              Approve
            </Button>
            <Button
              variant="soft"
              className="flex-1 text-danger"
              icon={<X className="size-4" />}
              disabled={!online || busy === order.id}
              onClick={() => setRejecting(true)}
            >
              Reject
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted">
            {online
              ? 'Needs the internet: a decision is made on the server.'
              : 'You are offline. Approving needs the internet.'}
          </p>
        </>
      )}
      {order.status === 'submitted' && !can.ok && (
        <p className="mt-3 rounded-xl bg-soft p-2 text-xs text-muted">{can.why}</p>
      )}
      {rejecting && (
        <RejectDialog order={order} maker={maker} onClose={() => setRejecting(false)} />
      )}
    </Card>
  )
}

const TONE = {
  success: 'bg-success-light text-success',
  warning: 'bg-warning-light text-warning',
  danger: 'bg-danger-light text-danger',
  neutral: 'bg-soft text-subtle',
} as const

function StepRow({
  icon,
  tone,
  title,
  who,
  line,
}: {
  icon: React.ReactNode
  tone: keyof typeof TONE
  title: string
  who: string
  line: string
}) {
  return (
    <li className="flex gap-3 border-t border-soft py-2.5 first:border-t-0">
      <span
        className={cx('flex size-8 shrink-0 items-center justify-center rounded-full', TONE[tone])}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-sm">
        <b className="block font-semibold text-ink">{title}</b>
        {who && <span className="block text-muted">{who}</span>}
        <span
          className={cx(
            'block text-xs font-semibold',
            tone === 'neutral' ? 'text-subtle' : TONE[tone].split(' ')[1],
          )}
        >
          {line}
        </span>
      </span>
    </li>
  )
}

// -----------------------------------------------------------------------------
// The inbox
// -----------------------------------------------------------------------------

/** A row of the inbox: the document and its second line (supplier, or where it is for). */
type InboxDoc = ApprovalDoc & { sub: string }

type Show = 'mine' | 'all' | 'decided'
const ROW_BUTTON = 'h-[34px] text-[13px]'
const ROW_ICON =
  'size-[34px] border border-border bg-surface text-danger max-sm:size-10! max-sm:flex-none!'
/** The row the arrow keys are on. */
const ROW_ACTIVE = 'sm:bg-buy-wash sm:shadow-[inset_3px_0_0_var(--color-buy)]'

export function ApprovalsScreen() {
  const settings = useSettings()
  const names = useStaffNames()
  const openItem = useOpen()
  const { rules, me, online } = useApprovalContext()
  const { decide, busy } = useDecide()
  const [show, setShow] = useState<Show>('mine')
  const [search, setSearch] = useState('')
  const [active, setActive] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<InboxDoc | null>(null)
  const data = useLive(
    async (r) => {
      const [orders, requests, decided, suppliers, locations] = await Promise.all([
        r.list('purchase_orders', {
          where: { status: 'submitted' },
          orderBy: { column: 'created_at' },
          limit: 1000,
        }),
        r.list('purchase_requests', {
          where: { status: 'submitted' },
          orderBy: { column: 'created_at' },
          limit: 1000,
        }),
        r.list('document_approvals', {
          orderBy: { column: 'created_at', direction: 'desc' },
          limit: 1000,
        }),
        r.list('suppliers', { limit: 2000 }),
        r.list('locations', { limit: 200 }),
      ])
      const supplier = new Map(suppliers.rows.map((s) => [s.id, s.name]))
      const place = new Map(locations.rows.map((l) => [l.id, l.name]))
      const fromOrder = (o: PurchaseOrderRow): InboxDoc => ({
        ...poDoc(o),
        sub: supplier.get(o.supplier_id ?? '') ?? 'No supplier',
      })
      const fromRequest = (q: PurchaseRequestRow): InboxDoc => ({
        ...requestDoc(q),
        sub: `Purchase request${q.location_id && place.get(q.location_id) ? ` · ${place.get(q.location_id)}` : ''}`,
      })
      const seen = [
        ...new Map(decided.rows.slice(0, 50).map((d) => [d.doc_id, d.doc_type])).entries(),
      ]
      const decidedDocs = await Promise.all(
        seen.map(async ([id, type]) => {
          if (type === 'purchase_order') {
            const o = await r.get('purchase_orders', id)
            return o ? fromOrder(o) : null
          }
          const q = await r.get('purchase_requests', id)
          return q ? fromRequest(q) : null
        }),
      )
      return {
        waiting: [...orders.rows.map(fromOrder), ...requests.rows.map(fromRequest)].sort((a, b) =>
          a.created_at.localeCompare(b.created_at),
        ),
        decisions: decided.rows,
        decidedOrders: decidedDocs.filter((o): o is InboxDoc => o !== null),
        // "Since yesterday" is measured from when the list was read, not during render.
        now: Date.now(),
      }
    },
    [],
    [...APPROVAL_TABLES, 'suppliers', 'locations'],
  ).data
  const name = (id: string | null | undefined) => (id && names[id]) || 'Someone'
  const decisionsOf = (o: InboxDoc): Decision[] =>
    (data?.decisions ?? [])
      .filter((d) => d.doc_id === o.id && d.doc_type === o.docType)
      .sort((a, b) => a.level - b.level)
  const yours = (o: InboxDoc) =>
    mayDecide(
      o,
      approvalSteps(rules, o.total_paisa, o.docType),
      decisionsOf(o),
      me,
      WORD[o.docType],
    ).ok
  const needle = search.trim().toLowerCase()
  const matches = (o: InboxDoc) =>
    !needle ||
    (o.number ?? '').toLowerCase().includes(needle) ||
    o.sub.toLowerCase().includes(needle)
  const mine = (data?.waiting ?? []).filter(yours)
  const rows =
    show === 'decided'
      ? (data?.decidedOrders ?? []).filter(matches)
      : (show === 'mine' ? mine : (data?.waiting ?? [])).filter(matches)
  const paged = usePaged(rows, `${show}:${search}`)
  const list = useRef<HTMLDivElement>(null)
  const activeRow = paged.rows.find((o) => o.id === active)
  const now = data?.now ?? 0
  const old = mine.filter((o) => now - Date.parse(o.created_at) > 86_400_000).length
  useListKeys({
    ids: paged.rows.map((o) => o.id),
    active,
    setActive,
    page: paged,
    list,
    open: () => activeRow && openItem(docPath(activeRow)),
    keys: {
      a: () => activeRow && online && yours(activeRow) && void decide(activeRow, 'approved', null),
      x: () => activeRow && online && yours(activeRow) && setRejecting(activeRow),
    },
  })
  return (
    <div>
      <PageHeader
        title="Purchase orders"
        subtitle={
          data ? (
            mine.length > 0 ? (
              <>
                <b className="font-semibold text-ink">
                  {mine.length} {mine.length === 1 ? 'waits' : 'wait'} for you
                </b>
                {old > 0 && (
                  <span className="font-semibold text-danger"> · {old} since yesterday</span>
                )}
                {' · '}
                {money(
                  mine.reduce((sum, o) => sum + o.total_paisa, 0),
                  settings,
                )}{' '}
                in all
              </>
            ) : (
              'Nothing waits for you.'
            )
          ) : undefined
        }
      />
      <SectionTabs section="purchase" />
      {!online && (
        <p className="mb-3 rounded-xl bg-warning-light px-3.5 py-2.5 text-sm font-semibold text-warning">
          You are offline. You can look, but approving needs the internet.
        </p>
      )}
      <ListToolbar>
        <Segmented
          label="Which orders"
          value={show}
          onChange={(v) => {
            setShow(v)
            setActive(null)
          }}
          className="max-sm:w-full"
          options={[
            { value: 'mine', label: 'For you' },
            { value: 'all', label: 'Everything waiting' },
            { value: 'decided', label: 'Decided' },
          ]}
        />
        <SearchInput
          listKeys
          className="max-sm:w-full sm:ml-auto sm:w-64"
          value={search}
          onChange={setSearch}
          placeholder="Number or supplier…"
        />
      </ListToolbar>
      <ListFrame ref={list}>
        {!data ? (
          <SkeletonTable cols={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Stamp className="size-6" />}
            title={
              needle
                ? 'Nothing matches'
                : show === 'decided'
                  ? 'Nothing decided yet'
                  : show === 'mine'
                    ? 'Nothing waits for you'
                    : 'Nothing is waiting for approval'
            }
            description={
              show === 'mine' && !needle
                ? 'Orders and requests you may approve show here: your step, within your limit.'
                : undefined
            }
          />
        ) : (
          <>
            <Table stack="cards">
              <thead>
                <tr>
                  <TH>Order or request</TH>
                  <TH>{show === 'decided' ? 'Decided' : 'Waiting'}</TH>
                  <TH>Step</TH>
                  <TH align="right">Total</TH>
                  <TH />
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((o) => {
                  const steps = approvalSteps(rules, o.total_paisa, o.docType)
                  const decisions = decisionsOf(o)
                  const step = nextStep(steps, decisions)
                  const to = docPath(o)
                  const word = WORD[o.docType]
                  const late = now - Date.parse(o.created_at) > 86_400_000
                  const last = decisions.at(-1)
                  const canHere = show !== 'decided' && yours(o)
                  return (
                    <TR
                      key={o.id}
                      onClick={() => openItem(to)}
                      aria-current={active === o.id ? 'true' : undefined}
                      className={cx(active === o.id && ROW_ACTIVE)}
                    >
                      <TD className="stack-title">
                        <RowTitle
                          tile={
                            <IconTile area="buy" size="sm">
                              {o.docType === 'purchase_order' ? (
                                <ClipboardList className="size-4" />
                              ) : (
                                <Hand className="size-4" />
                              )}
                            </IconTile>
                          }
                          title={
                            <Link
                              to={to}
                              className="hover:underline focus-visible:underline"
                              onClick={(e) => {
                                e.stopPropagation()
                                moveClick(to, 'open')(e)
                              }}
                            >
                              {o.number ??
                                `${word === 'order' ? 'Order' : 'Request'} (number on sync)`}
                            </Link>
                          }
                          sub={o.sub}
                        />
                      </TD>
                      <TD className="stack-r3">
                        {show === 'decided' && last ? (
                          <RowNote
                            main={`${last.decision === 'approved' ? 'Approved' : 'Rejected'} by ${name(last.decided_by)}`}
                            sub={timeAgo(last.created_at)}
                            tone={last.decision === 'rejected' ? 'danger' : 'success'}
                          />
                        ) : (
                          <RowNote
                            main={name(o.created_by)}
                            sub={
                              late
                                ? `Waiting since ${timeAgo(o.created_at)}`
                                : timeAgo(o.created_at)
                            }
                            tone={late ? 'danger' : undefined}
                            toneOn="sub"
                          />
                        )}
                      </TD>
                      <TD className="stack-end stack-r2">
                        {show === 'decided' ? (
                          <Badge tone={o.status === 'rejected' ? 'danger' : 'info'}>
                            {o.status === 'rejected'
                              ? 'Rejected'
                              : o.status === 'submitted'
                                ? 'Waiting'
                                : 'Approved'}
                          </Badge>
                        ) : (
                          <Badge tone="warning">
                            {step ? `Step ${step.level} of ${steps.length}` : 'Waiting'}
                          </Badge>
                        )}
                      </TD>
                      <TD align="right" className="stack-end stack-r1 whitespace-nowrap font-bold">
                        {money(o.total_paisa, settings)}
                      </TD>
                      <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                        <RowActions>
                          {canHere && (
                            <>
                              <Button
                                size="sm"
                                variant="soft"
                                className={ROW_BUTTON}
                                icon={<Check className="size-4" />}
                                aria-label={`Approve ${o.number ?? `the ${word}`}`}
                                loading={busy === o.id}
                                disabled={!online}
                                onClick={() => void decide(o, 'approved', null)}
                              >
                                Approve
                              </Button>
                              <IconButton
                                label={`Reject ${o.number ?? `the ${word}`}`}
                                className={ROW_ICON}
                                disabled={!online}
                                onClick={() => setRejecting(o)}
                              >
                                <X className="size-4" />
                              </IconButton>
                            </>
                          )}
                        </RowActions>
                      </TD>
                    </TR>
                  )
                })}
              </tbody>
            </Table>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
              <span>
                {show === 'decided'
                  ? 'Newest first.'
                  : show === 'mine'
                    ? 'Oldest first. Only what you may approve: your step, within your limit.'
                    : 'Oldest first.'}
              </span>
              <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open
                {show !== 'decided' && (
                  <>
                    {' '}
                    <Kbd>A</Kbd> approve <Kbd>X</Kbd> reject
                  </>
                )}
              </span>
            </div>
            <Pagination
              offset={paged.offset}
              limit={paged.limit}
              total={paged.total}
              onChange={paged.setOffset}
            />
          </>
        )}
      </ListFrame>
      {rejecting && (
        <RejectDialog
          order={rejecting}
          maker={name(rejecting.created_by)}
          onClose={() => setRejecting(null)}
        />
      )}
    </div>
  )
}
